import formatBytes from "tools/formats/formatBytes";
import { prettifyFileExtension } from "tools/file/prettifyFileExtension";

export interface FileValidationOptions {
  sizeLimit: number;
  acceptedTypes?: string[];
  acceptedExtensions?: string[];
}

export interface FileValidationResult {
  isValid: boolean;
  errorMessage?: string;
}

export const validateFileBeforeUpload = (
  file: File | null,
  { sizeLimit, acceptedTypes, acceptedExtensions }: FileValidationOptions
): FileValidationResult => {
  if (!file) {
    return {
      isValid: false,
      errorMessage: "No file selected",
    };
  }

  // check size
  if (file.size > sizeLimit) {
    return {
      isValid: false,
      errorMessage: `File size exceeds the ${formatBytes(sizeLimit, -1)} limit`,
    };
  }

  const formatError = () =>
    FILE_FORMAT_ERROR(
      acceptedExtensions ??
        (acceptedTypes ?? []).map((type) => type.split("/").pop() ?? type)
    );

  if (acceptedExtensions && acceptedExtensions.length > 0) {
    if (!acceptedExtensions.includes(prettifyFileExtension(file))) {
      return {
        isValid: false,
        errorMessage: formatError(),
      };
    }
  }

  // check type
  if (acceptedTypes && acceptedTypes.length > 0) {
    if (!acceptedTypes.includes(file.type)) {
      return {
        isValid: false,
        errorMessage: formatError(),
      };
    }
  }

  return {
    isValid: true,
  };
};

export const formatAcceptedExtensions = (
  acceptedExtensions: string[]
): string => {
  if (acceptedExtensions.length === 1) {
    return acceptedExtensions[0].toUpperCase();
  }

  if (acceptedExtensions.length === 2) {
    return acceptedExtensions.map((ext) => ext.toUpperCase()).join(" or ");
  }

  return `${acceptedExtensions
    .slice(0, -1)
    .map((ext) => ext.toUpperCase())
    .join(", ")} or ${acceptedExtensions.slice(-1)[0].toUpperCase()}`;
};

export const FILE_FORMAT_ERROR = (
  acceptedExtensions: string[],
  prefix?: string
) => {
  const defaultPrefix = "Unsupported file type. Please upload a ";
  const defaultEnd = " file";
  const formattedExtensions = formatAcceptedExtensions(acceptedExtensions);

  return `${prefix || defaultPrefix}${formattedExtensions}${defaultEnd}`;
};

export const DEFAULT_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
];

// Signatures accept images plus PDF documents. Single source of truth shared by
// every signature upload path (Show Settings create/edit + the sign-task modal)
// so the allowed types can't drift between them.
export const SIGNATURE_ACCEPTED_TYPES = [
  ...DEFAULT_IMAGE_TYPES,
  "application/pdf",
];

export const PDF_ACCEPTED_TYPES = ["application/pdf"];

export const PDF_ACCEPTED_EXTENSIONS = ["pdf"];
